import { useMemo } from 'react';
import { DimTree } from './DimTree';

/**
 * KPI selector backed by DimTree. Builds a synthetic tree from the supplied KPIs: a "KPIs" root
 * whose children are grpName group nodes (each holding its KPI leaves) interleaved with ungrouped
 * KPIs as top-level leaves, all in the supplied order. Node ids are KPI codes (leaves) and
 * "grp:<name>" (groups) — DimTree is id-agnostic — so selection round-trips as KPI codes.
 */
export const KpiSelect = ({
  kpis, // Ordered array of KPI objects ({ code, displayName, grpName? })
  selectedCodes, // Array of selected KPI codes
  onChange, // Called with the new array of selected KPI codes
  displayName = 'KPIs', // Label of the tree select
  variant, // The MUI TextField variant for the select input ('outlined' | 'filled')
  error, // Whether the field is in an error state
  helperText, // Helper / error text shown beneath the field
  onBlur, // Called when the dropdown closes (blur-like)
}) => {
  const kpiTree = useMemo(() => {
    const children = [];
    const groupNodes = {};
    kpis.forEach((kpi) => {
      if (kpi.grpName) {
        let group = groupNodes[kpi.grpName];
        if (!group) {
          group = { id: 'grp:' + kpi.grpName, name: kpi.grpName, children: [] };
          groupNodes[kpi.grpName] = group;
          children.push(group);
        }
        group.children.push({ id: kpi.code, name: kpi.displayName });
      } else {
        children.push({ id: kpi.code, name: kpi.displayName });
      }
    });
    return { id: 'kpis', name: displayName, children };
  }, [kpis, displayName]);

  return (
    <DimTree
      displayName={displayName}
      variant={variant}
      treeData={kpiTree}
      selectedIds={selectedCodes}
      onFilterChange={onChange}
      error={error}
      helperText={helperText}
      onBlur={onBlur}
    />
  );
};
